/**
 * ERC Shorted Components Validator
 *
 * This validator checks for any 2-pin component that has both pins connected to the same net,
 * indicating the component is shorted out and not functioning as intended.
 *
 * Feature-gated via DrcEnabledCheckers.ErcShortedComponentsValidator
 */
import { elementMatchesPartType, ReviewType } from "@buildwithflux/models";
import { getProblemTypeKeyFromReviewType } from "../../drc/types";
import { ErcSeverity } from "../types";
import { createErcProblem, getPinsForNet } from "./ercUtils";
/**
 * Validator that checks for shorted components.
 * Reports any 2-pin component with both pins connected to the same net.
 * Excludes part type "Antenna" (antennas are often intentionally shorted at DC).
 */
export class ErcShortedComponentsValidator {
    reviewType = ReviewType.DesignRuleCheckErcShortedComponents;
    problemTypeKey = getProblemTypeKeyFromReviewType(this.reviewType);
    defaultHidden = false;
    async checkForProblems(inputs) {
        const foundProblems = [];
        const reportedElements = new Set(); // Track reported elements to avoid duplicates
        for (const [netHash, terminals] of Object.entries(inputs.netMap)) {
            const pins = getPinsForNet(inputs, terminals);
            if (pins.length < 2) {
                continue;
            }
            // Group pins by element UID
            const pinsByElement = new Map();
            for (const pin of pins) {
                if (!pinsByElement.has(pin.elementUid)) {
                    pinsByElement.set(pin.elementUid, []);
                }
                pinsByElement.get(pin.elementUid).push(pin);
            }
            // Check each element for shorted 2-pin components
            for (const [elementUid, elementPins] of pinsByElement) {
                if (reportedElements.has(elementUid)) {
                    continue;
                }
                const element = inputs.elements[elementUid];
                if (!element) {
                    continue;
                }
                const terminalCount = element?.part_version_data_cache?.terminals
                    ? Object.keys(element.part_version_data_cache.terminals).length
                    : 0;
                // Only report for components with exactly 2 pins that have both pins on this net
                if (terminalCount !== 2 || elementPins.length !== 2) {
                    continue;
                }
                const [pin1] = elementPins;
                if (!pin1) {
                    continue;
                }
                if (elementMatchesPartType(element, "Antenna")) {
                    continue;
                }
                const componentName = pin1.elementLabel || element?.part_version_data_cache?.name || "Component";
                const message = `Component shorted. Both pins of ${componentName} are connected to the same net.`;
                const netName = inputs.netNameByNetId?.[netHash] ?? netHash;
                const problem = createErcProblem(this.problemTypeKey, `${this.problemTypeKey}_${netHash}_shorted_${elementUid}`, message, [elementUid], [componentName], ErcSeverity.WARNING);
                problem.metadata = { ...problem.metadata, ercNet: netName };
                foundProblems.push(problem);
                reportedElements.add(elementUid);
            }
        }
        return {
            error: false,
            problemTypeKey: this.problemTypeKey,
            foundProblems,
        };
    }
}
//# sourceMappingURL=ErcShortedComponentsValidator.js.map